import fs from 'node:fs'
import path from 'node:path'
import { isFieldType, isFormFieldType } from '@core-engine/schema/constants.js'
import {
  validateEntitySchema,
  validateFormSchema,
  validateSingleSchema
} from '@core-engine/schema/schema-reader.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import type {
  FieldType,
  FormField,
  FormFieldType,
  FormSchema,
  Schema,
  SchemaField,
  SchemaSlotLayout
} from '@core-engine/schema/types.js'
import { stringifyProjectJson } from '@core-engine/utils/json-format.js'
import { isRecord } from '@core-engine/utils/record.js'
import { toCamelCase } from '@core-engine/utils/string.js'

export type GeneratedSchemaKind = 'entity' | 'single' | 'form'
export type RelationshipTargetKind = GeneratedSchemaKind | 'user'

export type GeneratedSchemaField = SchemaField | FormField
export type AddFieldSlot = 'main' | 'sidebar'

export type AuthoredEntityOrSingleSchema = Omit<Schema, 'fields'> & {
  fields?: SchemaField[]
  slot?: SchemaSlotLayout
}

export type AuthoredGeneratedSchema = AuthoredEntityOrSingleSchema | FormSchema

export interface LoadedAuthoredGeneratedSchema {
  kind: GeneratedSchemaKind
  schema: AuthoredGeneratedSchema
  filePath: string
  name: string
}

export type AddFieldNameScope = 'schema' | 'list-item'

export interface AddFieldPlacementTarget {
  id: string
  label: string
  scope: AddFieldNameScope
  getFields: () => GeneratedSchemaField[]
}

export interface AddFieldOrder {
  before?: string
  after?: string
}

export interface AddFieldOption {
  value: string
  label: string
}

export interface BuildLeafFieldInput {
  kind: GeneratedSchemaKind
  type: string
  name: string
  label: string
  required?: boolean
  multiple?: boolean
  creatable?: boolean
  options?: AddFieldOption[]
  relationship?: string
}

export const ENTITY_SINGLE_ADD_FIELD_TYPES = [
  'string',
  'email',
  'varchar',
  'text',
  'markdown',
  'richtext',
  'number',
  'decimal',
  'boolean',
  'date',
  'timestamp',
  'time',
  'image',
  'video',
  'media',
  'gallery',
  'icon',
  'select',
  'relationship',
  'list',
  'group',
  'section',
  'tabs',
  'separator'
] as const satisfies readonly FieldType[]

export const FORM_ADD_FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'url',
  'date',
  'select',
  'radio',
  'checkbox',
  'file'
] as const satisfies readonly FormFieldType[]

export const FORM_OPTION_FIELD_TYPES = ['select', 'radio', 'checkbox'] as const
const FORM_OPTION_FIELD_TYPE_SET = new Set<string>(FORM_OPTION_FIELD_TYPES)
const FORM_REQUIRED_OPTION_FIELD_TYPES = new Set<string>(['select', 'radio'])

export const NON_INTERACTIVE_CONTAINER_TYPES = new Set(['group', 'section', 'tabs', 'list'])

const SYSTEM_FIELD_NAMES = new Set([
  'id',
  'uuid',
  'serial',
  'slug',
  'createdAt',
  'updatedAt',
  'published',
  'submittedAt',
  'ipAddress',
  'userAgent',
  'createdBy',
  'updatedBy',
  'createdByUser',
  'updatedByUser'
])

const SCHEMA_LAYOUT_TYPES = new Set(['group', 'section', 'tabs', 'separator'])
const FORM_LAYOUT_TYPES = new Set(['group'])

export function normalizeAddFieldSlot(slot: string | undefined): AddFieldSlot | undefined {
  if (slot === undefined) {
    return undefined
  }

  if (slot === 'main' || slot === 'sidebar') {
    return slot
  }

  throw new Error(`Invalid --slot "${slot}". Use "main" or "sidebar".`)
}

function parseJsonObject(content: string, filePath: string): Record<string, unknown> {
  let parsed: unknown
  try {
    parsed = JSON.parse(content)
  } catch {
    throw new Error(`Failed to parse JSON schema: ${filePath}`)
  }

  if (!isRecord(parsed)) {
    throw new Error(`Schema file must contain a JSON object: ${filePath}`)
  }

  return parsed
}

export function loadAuthoredGeneratedSchema(
  schemasDir: string,
  name: string
): LoadedAuthoredGeneratedSchema {
  const filePaths = [
    path.join(schemasDir, `${name}.json`),
    path.join(schemasDir, 'forms', `${name}.json`)
  ].filter((filePath) => fs.existsSync(filePath))

  if (filePaths.length === 0) {
    throw new Error(
      `Schema "${name}" not found. Looked in:\n  - ${path.join(schemasDir, `${name}.json`)}\n  - ${path.join(schemasDir, 'forms', `${name}.json`)}`
    )
  }

  if (filePaths.length > 1) {
    throw new Error(
      `Found multiple schemas named "${name}". Keep only one file: ${filePaths.join(', ')}`
    )
  }

  const filePath = filePaths[0]!
  const parsed = parseJsonObject(fs.readFileSync(filePath, 'utf-8'), filePath)
  const type = parsed.type

  if (type === undefined) {
    throw new Error(
      `Schema "${name}" is missing required top-level "type". Expected "entity", "single", or "form".`
    )
  }

  if (type === 'form') {
    return {
      kind: 'form',
      schema: parsed as unknown as FormSchema,
      filePath,
      name
    }
  }

  if (type === 'single') {
    return {
      kind: 'single',
      schema: parsed as unknown as AuthoredEntityOrSingleSchema,
      filePath,
      name
    }
  }

  if (type !== undefined && type !== 'entity') {
    throw new Error(
      `Schema "${name}" has unsupported type "${String(type)}". Expected "entity", "single", or "form".`
    )
  }

  return {
    kind: 'entity',
    schema: parsed as unknown as AuthoredEntityOrSingleSchema,
    filePath,
    name
  }
}

export function writeAuthoredGeneratedSchema(loaded: LoadedAuthoredGeneratedSchema): void {
  fs.writeFileSync(loaded.filePath, stringifyProjectJson(loaded.schema), 'utf-8')
}

export function deriveFieldNameFromLabel(label: string): string {
  const stripped = label
    .trim()
    .replace(/['"]/g, '')
    .replace(/[^A-Za-z0-9_\s-]+/g, ' ')

  return toCamelCase(stripped.trim() || 'field')
}

export function validateGeneratedFieldName(name: string): string[] {
  const errors: string[] = []
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
    errors.push(
      `Field name "${name}" is invalid. Use letters, numbers, and underscores, starting with a letter.`
    )
  }

  if (SYSTEM_FIELD_NAMES.has(name)) {
    errors.push(`Field name "${name}" is reserved by BetterStart.`)
  }

  return errors
}

export function parseAddFieldOption(value: string): AddFieldOption {
  const separatorIndex = value.indexOf(':')
  if (separatorIndex <= 0 || separatorIndex === value.length - 1) {
    throw new Error(`Invalid option "${value}". Use value:Label.`)
  }

  const optionValue = value.slice(0, separatorIndex).trim()
  const label = value.slice(separatorIndex + 1).trim()

  if (!optionValue || !label) {
    throw new Error(`Invalid option "${value}". Use value:Label.`)
  }

  return {
    value: optionValue,
    label
  }
}

export function parseAddFieldOptions(values: string[] | undefined): AddFieldOption[] {
  const options = (values ?? []).map(parseAddFieldOption)
  const seen = new Set<string>()
  for (const option of options) {
    if (seen.has(option.value)) {
      throw new Error(`Duplicate option value "${option.value}".`)
    }
    seen.add(option.value)
  }
  return options
}

export function isSupportedAddFieldType(kind: GeneratedSchemaKind, type: string): boolean {
  return kind === 'form'
    ? (FORM_ADD_FIELD_TYPES as readonly string[]).includes(type)
    : (ENTITY_SINGLE_ADD_FIELD_TYPES as readonly string[]).includes(type)
}

export function buildLeafField(input: BuildLeafFieldInput): GeneratedSchemaField {
  const errors = [...validateGeneratedFieldName(input.name), ...validateLeafFieldInput(input)]

  if (errors.length > 0) {
    throw new Error(errors.join('\n'))
  }

  const field: Record<string, unknown> = {
    name: input.name,
    type: input.type,
    label: input.label
  }

  if (input.required) {
    field.required = true
  }

  if (input.kind === 'form') {
    if (input.multiple && input.type === 'file') {
      field.multiple = true
    }
    if (input.options && input.options.length > 0) {
      field.options = input.options
    }
    return field as unknown as FormField
  }

  if (input.multiple && ['select', 'relationship'].includes(input.type)) {
    field.multiple = true
  }

  if (input.creatable && input.type === 'select') {
    field.creatable = true
  }

  if (input.options && input.options.length > 0) {
    field.options = input.options
  }

  if (input.relationship && input.type === 'relationship') {
    field.relationship = input.relationship
  }

  return field as unknown as SchemaField
}

function validateLeafFieldInput(input: BuildLeafFieldInput): string[] {
  const errors: string[] = []

  if (!isSupportedAddFieldType(input.kind, input.type)) {
    errors.push(`Field type "${input.type}" is not supported for ${input.kind} schemas.`)
    return errors
  }

  if (input.kind === 'form') {
    if (!isFormFieldType(input.type)) {
      errors.push(`Form field type "${input.type}" is not supported.`)
    }
    if (input.creatable) {
      errors.push('--creatable is only supported for Entity and Single select fields.')
    }
    if (input.relationship) {
      errors.push('Form schemas do not support relationship fields.')
    }
    if (input.multiple && input.type !== 'file') {
      errors.push('--multiple is only supported for file form fields.')
    }
    if (FORM_REQUIRED_OPTION_FIELD_TYPES.has(input.type) && !input.options?.length) {
      errors.push(`Form ${input.type} fields require at least one --option value:Label.`)
    }
    if (input.options?.length && !FORM_OPTION_FIELD_TYPE_SET.has(input.type)) {
      errors.push('--option is only supported for select, radio, and checkbox form fields.')
    }
    return errors
  }

  if (!isFieldType(input.type)) {
    errors.push(`Schema field type "${input.type}" is not supported.`)
  }

  if (input.type === 'select') {
    if (!input.creatable && !input.options?.length) {
      errors.push('Static select fields require at least one --option value:Label.')
    }
  } else if (input.creatable) {
    errors.push('--creatable is only supported for select fields.')
  }

  if (input.multiple && !['select', 'relationship'].includes(input.type)) {
    errors.push('--multiple is only supported for select and relationship fields.')
  }

  if (input.type === 'relationship' && !input.relationship) {
    errors.push('Relationship fields require --relationship <schema>.')
  }

  if (input.relationship && input.type !== 'relationship') {
    errors.push('--relationship is only supported for relationship fields.')
  }

  if (input.options?.length && input.type !== 'select') {
    errors.push('--option is only supported for select fields on Entity and Single schemas.')
  }

  return errors
}

export function buildPlacementTargets(
  loaded: LoadedAuthoredGeneratedSchema
): AddFieldPlacementTarget[] {
  if (loaded.kind === 'form') {
    return buildFormPlacementTargets(loaded.schema as FormSchema)
  }

  return buildSchemaPlacementTargets(loaded.schema as AuthoredEntityOrSingleSchema)
}

function buildSchemaPlacementTargets(
  schema: AuthoredEntityOrSingleSchema
): AddFieldPlacementTarget[] {
  const targets: AddFieldPlacementTarget[] = []

  if (schema.slot) {
    targets.push({
      id: 'root:slot:main',
      label: 'top-level slot.main.fields',
      scope: 'schema',
      getFields: () => ensureSchemaSlotFields(schema, 'main')
    })
    targets.push({
      id: 'root:slot:sidebar',
      label: 'top-level slot.sidebar.fields',
      scope: 'schema',
      getFields: () => ensureSchemaSlotFields(schema, 'sidebar')
    })
    collectSchemaNestedTargets(readSchemaSlotFields(schema.slot), 'slot', 'schema', targets)
    return targets
  }

  targets.push({
    id: 'root:fields',
    label: 'top-level fields',
    scope: 'schema',
    getFields: () => ensureSchemaFields(schema)
  })
  collectSchemaNestedTargets(schema.fields ?? [], 'fields', 'schema', targets)
  return targets
}

function buildFormPlacementTargets(schema: FormSchema): AddFieldPlacementTarget[] {
  const targets: AddFieldPlacementTarget[] = []

  if (schema.steps) {
    for (const step of schema.steps) {
      targets.push({
        id: `step:${step.name}`,
        label: `step "${step.name}" fields`,
        scope: 'schema',
        getFields: () => step.fields
      })
      collectFormNestedTargets(step.fields, `step:${step.name}`, 'schema', targets)
    }
    return targets
  }

  targets.push({
    id: 'root:fields',
    label: 'top-level fields',
    scope: 'schema',
    getFields: () => ensureFormFields(schema)
  })
  collectFormNestedTargets(schema.fields ?? [], 'fields', 'schema', targets)
  return targets
}

function collectSchemaNestedTargets(
  fields: SchemaField[],
  pathPrefix: string,
  scope: AddFieldNameScope,
  targets: AddFieldPlacementTarget[]
): void {
  for (const field of fields) {
    const fieldPath = `${pathPrefix}.${field.name || field.type}`
    if (field.type === 'group' || field.type === 'section' || field.type === 'list') {
      const childScope: AddFieldNameScope = field.type === 'list' ? 'list-item' : scope
      targets.push({
        id: `${fieldPath}.fields`,
        label: `${fieldPath}.fields`,
        scope: childScope,
        getFields: () => ensureSchemaChildFields(field)
      })
      collectSchemaNestedTargets(field.fields ?? [], fieldPath, childScope, targets)
    }

    if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        const tabPath = `${fieldPath}.tabs.${tab.name}`
        if (tab.slot) {
          targets.push({
            id: `${tabPath}.slot:main`,
            label: `${tabPath}.slot.main.fields`,
            scope,
            getFields: () => ensureTabSlotFields(tab, 'main')
          })
          targets.push({
            id: `${tabPath}.slot:sidebar`,
            label: `${tabPath}.slot.sidebar.fields`,
            scope,
            getFields: () => ensureTabSlotFields(tab, 'sidebar')
          })
          collectSchemaNestedTargets(readSchemaSlotFields(tab.slot), tabPath, scope, targets)
        } else {
          targets.push({
            id: `${tabPath}.fields`,
            label: `${tabPath}.fields`,
            scope,
            getFields: () => ensureTabFields(tab)
          })
          collectSchemaNestedTargets(tab.fields ?? [], tabPath, scope, targets)
        }
      }
    }
  }
}

function collectFormNestedTargets(
  fields: FormField[],
  pathPrefix: string,
  scope: AddFieldNameScope,
  targets: AddFieldPlacementTarget[]
): void {
  for (const field of fields) {
    const fieldPath = `${pathPrefix}.${field.name || field.type}`
    if (field.type === 'group' || field.type === 'list') {
      const childScope: AddFieldNameScope = field.type === 'list' ? 'list-item' : scope
      targets.push({
        id: `${fieldPath}.fields`,
        label: `${fieldPath}.fields`,
        scope: childScope,
        getFields: () => ensureFormChildFields(field)
      })
      collectFormNestedTargets(field.fields ?? [], fieldPath, childScope, targets)
    }
  }
}

export function resolveDefaultPlacementTarget(
  loaded: LoadedAuthoredGeneratedSchema,
  options: { slot?: string; step?: string; targetId?: string } = {}
): AddFieldPlacementTarget {
  const targets = buildPlacementTargets(loaded)
  const slot = normalizeAddFieldSlot(options.slot)

  if (options.targetId) {
    const target = targets.find((candidate) => candidate.id === options.targetId)
    if (!target) {
      throw new Error(`Placement target "${options.targetId}" was not found.`)
    }
    return target
  }

  if (loaded.kind === 'form') {
    if (slot) {
      throw new Error('--slot is only supported for Entity and Single schemas.')
    }

    const formSchema = loaded.schema as FormSchema
    if (formSchema.steps) {
      if (!options.step) {
        throw new Error('This form has steps. Use --step <name> to choose the target step.')
      }
      const target = targets.find((candidate) => candidate.id === `step:${options.step}`)
      if (!target) {
        throw new Error(`Form step "${options.step}" was not found.`)
      }
      return target
    }

    return targets.find((candidate) => candidate.id === 'root:fields') ?? targets[0]!
  }

  const schema = loaded.schema as AuthoredEntityOrSingleSchema
  if (schema.slot) {
    const targetSlot = slot ?? 'main'
    const target = targets.find((candidate) => candidate.id === `root:slot:${targetSlot}`)
    if (!target) {
      throw new Error(`Placement target "root:slot:${targetSlot}" was not found.`)
    }
    return target
  }

  const target = targets.find((candidate) => candidate.id === 'root:fields') ?? targets[0]!
  return {
    ...target,
    getFields: () => target.getFields()
  }
}

export function applySlotToField(
  loaded: LoadedAuthoredGeneratedSchema,
  target: AddFieldPlacementTarget,
  field: GeneratedSchemaField,
  slot?: string
): GeneratedSchemaField {
  const normalizedSlot = normalizeAddFieldSlot(slot)
  if (!normalizedSlot || loaded.kind === 'form') {
    return field
  }

  const schema = loaded.schema as AuthoredEntityOrSingleSchema
  if (schema.slot || target.id !== 'root:fields') {
    return field
  }

  return {
    ...(field as SchemaField),
    slot: normalizedSlot
  }
}

export function insertFieldIntoTarget(
  target: AddFieldPlacementTarget,
  field: GeneratedSchemaField,
  order: AddFieldOrder = {}
): void {
  if (order.before && order.after) {
    throw new Error('Use either --before or --after, not both.')
  }

  const fields = target.getFields()

  if (order.before) {
    const index = fields.findIndex((candidate) => fieldNameOf(candidate) === order.before)
    if (index < 0) {
      throw new Error(`Field "${order.before}" was not found in ${target.label}.`)
    }
    fields.splice(index, 0, field)
    return
  }

  if (order.after) {
    const index = fields.findIndex((candidate) => fieldNameOf(candidate) === order.after)
    if (index < 0) {
      throw new Error(`Field "${order.after}" was not found in ${target.label}.`)
    }
    fields.splice(index + 1, 0, field)
    return
  }

  fields.push(field)
}

export function validateAuthoredGeneratedSchema(loaded: LoadedAuthoredGeneratedSchema): string[] {
  const schemaValidationErrors =
    loaded.kind === 'form'
      ? validateFormSchema(loaded.schema as FormSchema)
      : loaded.kind === 'single'
        ? validateSingleSchema(loaded.schema as Schema)
        : validateEntitySchema(loaded.schema as Schema)

  return [...schemaValidationErrors, ...collectFieldNameValidationErrors(loaded)]
}

export function isRequiredPersistedSchemaField(
  loaded: LoadedAuthoredGeneratedSchema,
  field: GeneratedSchemaField,
  target: AddFieldPlacementTarget
): boolean {
  if (loaded.kind === 'form' || target.scope === 'list-item' || !field.required) {
    return false
  }

  return isPersistedSchemaField(field as SchemaField)
}

export function readRelationshipTargetKind(
  schemasDir: string,
  schemaName: string
): RelationshipTargetKind {
  if (isUserRelationshipTarget(schemaName)) {
    return 'user'
  }

  return loadAuthoredGeneratedSchema(schemasDir, schemaName).kind
}

export function getSchemaDisplayName(loaded: LoadedAuthoredGeneratedSchema): string {
  const label = isRecord(loaded.schema) ? loaded.schema.label : undefined
  return typeof label === 'string' && label.trim() ? label : loaded.name
}

function collectFieldNameValidationErrors(loaded: LoadedAuthoredGeneratedSchema): string[] {
  const errors: string[] = []
  const occurrences = new Map<string, string[]>()

  if (loaded.kind === 'form') {
    const schema = loaded.schema as FormSchema
    const rootFields = schema.steps
      ? schema.steps.flatMap((step) => step.fields)
      : (schema.fields ?? [])
    collectFormPersistedNames(rootFields, 'fields', occurrences, errors)
  } else {
    const schema = loaded.schema as AuthoredEntityOrSingleSchema
    collectSchemaPersistedNames(getAuthoredSchemaRootFields(schema), 'fields', occurrences, errors)
  }

  for (const [name, paths] of occurrences) {
    if (paths.length > 1) {
      errors.push(`Duplicate persisted field name "${name}" found at ${paths.join(', ')}.`)
    }
  }

  return errors
}

function collectSchemaPersistedNames(
  fields: SchemaField[],
  pathPrefix: string,
  occurrences: Map<string, string[]>,
  errors: string[]
): void {
  for (const field of fields) {
    const fieldPath = `${pathPrefix}.${field.name || field.type}`
    if (field.name && isPersistedSchemaField(field)) {
      addOccurrence(occurrences, field.name, fieldPath)
    }

    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      collectSchemaPersistedNames(field.fields, fieldPath, occurrences, errors)
    }

    if (field.type === 'tabs' && field.tabs) {
      for (const tab of field.tabs) {
        collectSchemaPersistedNames(
          getTabFields(tab),
          `${fieldPath}.tabs.${tab.name}`,
          occurrences,
          errors
        )
      }
    }

    if (field.type === 'list' && field.fields) {
      collectLocalListNameErrors(field.fields, `${fieldPath}.fields`, errors)
    }
  }
}

function collectFormPersistedNames(
  fields: FormField[],
  pathPrefix: string,
  occurrences: Map<string, string[]>,
  errors: string[]
): void {
  for (const field of fields) {
    const fieldPath = `${pathPrefix}.${field.name || field.type}`
    if (field.name && isPersistedFormField(field)) {
      addOccurrence(occurrences, field.name, fieldPath)
    }

    if (field.type === 'group' && field.fields) {
      collectFormPersistedNames(field.fields, fieldPath, occurrences, errors)
    }

    if (field.type === 'list' && field.fields) {
      collectLocalListNameErrors(field.fields, `${fieldPath}.fields`, errors)
    }
  }
}

function collectLocalListNameErrors(
  fields: GeneratedSchemaField[],
  pathPrefix: string,
  errors: string[]
): void {
  const seen = new Map<string, string>()

  for (const field of fields) {
    const name = fieldNameOf(field)
    const fieldPath = `${pathPrefix}.${name || fieldTypeOf(field)}`
    if (name) {
      const previous = seen.get(name)
      if (previous) {
        errors.push(`Duplicate list item field name "${name}" found at ${previous}, ${fieldPath}.`)
      } else {
        seen.set(name, fieldPath)
      }
    }

    if (hasChildFields(field)) {
      collectLocalListNameErrors(field.fields, `${fieldPath}.fields`, errors)
    }
  }
}

function addOccurrence(occurrences: Map<string, string[]>, name: string, fieldPath: string): void {
  const paths = occurrences.get(name) ?? []
  paths.push(fieldPath)
  occurrences.set(name, paths)
}

function isPersistedSchemaField(field: SchemaField): boolean {
  return !SCHEMA_LAYOUT_TYPES.has(field.type)
}

function isPersistedFormField(field: FormField): boolean {
  return !FORM_LAYOUT_TYPES.has(field.type)
}

function fieldNameOf(field: GeneratedSchemaField): string | undefined {
  return typeof field.name === 'string' ? field.name : undefined
}

function fieldTypeOf(field: GeneratedSchemaField): string {
  return typeof field.type === 'string' ? field.type : 'field'
}

function hasChildFields(field: GeneratedSchemaField): field is GeneratedSchemaField & {
  fields: GeneratedSchemaField[]
} {
  return Array.isArray(field.fields)
}

function ensureSchemaFields(schema: AuthoredEntityOrSingleSchema): SchemaField[] {
  if (!Array.isArray(schema.fields)) {
    schema.fields = []
  }
  return schema.fields
}

function ensureSchemaChildFields(field: SchemaField): SchemaField[] {
  if (!Array.isArray(field.fields)) {
    field.fields = []
  }
  return field.fields
}

function ensureFormFields(schema: FormSchema): FormField[] {
  if (!Array.isArray(schema.fields)) {
    schema.fields = []
  }
  return schema.fields
}

function ensureFormChildFields(field: FormField): FormField[] {
  if (!Array.isArray(field.fields)) {
    field.fields = []
  }
  return field.fields
}

function ensureSchemaSlotFields(
  schema: AuthoredEntityOrSingleSchema,
  slot: 'main' | 'sidebar'
): SchemaField[] {
  if (!schema.slot) {
    schema.slot = {}
  }

  if (!schema.slot[slot]) {
    schema.slot[slot] = { fields: [] }
  }

  if (!Array.isArray(schema.slot[slot]?.fields)) {
    schema.slot[slot] = { fields: [] }
  }

  return schema.slot[slot]!.fields
}

function ensureTabFields(tab: NonNullable<SchemaField['tabs']>[number]): SchemaField[] {
  if (!Array.isArray(tab.fields)) {
    tab.fields = []
  }
  return tab.fields
}

function ensureTabSlotFields(
  tab: NonNullable<SchemaField['tabs']>[number],
  slot: 'main' | 'sidebar'
): SchemaField[] {
  if (!tab.slot) {
    tab.slot = {}
  }

  if (!tab.slot[slot]) {
    tab.slot[slot] = { fields: [] }
  }

  if (!Array.isArray(tab.slot[slot]?.fields)) {
    tab.slot[slot] = { fields: [] }
  }

  return tab.slot[slot]!.fields
}

function readSchemaSlotFields(slot: SchemaSlotLayout | undefined): SchemaField[] {
  return [
    ...(Array.isArray(slot?.main?.fields) ? slot.main.fields : []),
    ...(Array.isArray(slot?.sidebar?.fields) ? slot.sidebar.fields : [])
  ]
}

function getAuthoredSchemaRootFields(schema: AuthoredEntityOrSingleSchema): SchemaField[] {
  return schema.slot ? readSchemaSlotFields(schema.slot) : (schema.fields ?? [])
}

function getTabFields(tab: NonNullable<SchemaField['tabs']>[number]): SchemaField[] {
  return tab.slot ? readSchemaSlotFields(tab.slot) : (tab.fields ?? [])
}
